import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import Drawer from '../../components/Drawer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Autocomplete } from '../../components/ui/autocomplete';
import FilterDrawer, { toFilterAction } from '../../components/FilterDrawer';
import useReportFilters from '../../utils/useReportFilters';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { toReportExportAction } from '../../components/ReportExportButton';
import { DateRangePicker } from '../../components/ui/date-range-picker';
import { reportsService } from '../../services/reportsService';
import { downloadReportCsv } from '../../utils/reportExport';
import {
    productsService,
    suppliesService,
    warehousesService,
    movementTypesService,
} from '../../services/productionService';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import ProductionOrderDocumentRef from '../../components/reports/ProductionOrderDocumentRef';
import ReportStockItemLink from '../../components/reports/ReportStockItemLink';
import { cn, formatDateBR, formatQuantity, formatSignedQuantity, localDateISO } from '../../lib/utils';

function currentMonthPeriod() {
    const now = new Date();
    return {
        from: localDateISO(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: localDateISO(now),
    };
}

function StockMovementsReport() {
    usePageTitle('Movimentação de estoque');
    const { toast, toastApiError } = useToast();
    const filters = useReportFilters(() => ({
        period: currentMonthPeriod(),
        product: '',
        supply: '',
        movementTypeId: '',
        warehouseId: '',
    }));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const [extractDrawerOpen, setExtractDrawerOpen] = useState(false);
    const [drawerExtractItem, setDrawerExtractItem] = useState(null);
    const [drawerItemLabel, setDrawerItemLabel] = useState('');
    const [extractData, setExtractData] = useState(null);
    const [extractLoading, setExtractLoading] = useState(false);

    const [adjustmentDrawerOpen, setAdjustmentDrawerOpen] = useState(false);
    const [adjustmentLoading, setAdjustmentLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [supplies, setSupplies] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [movementTypes, setMovementTypes] = useState([]);
    const [defaultMovementTypeId, setDefaultMovementTypeId] = useState(null);
    const [adjustItemKey, setAdjustItemKey] = useState(null);
    const [adjustSelectedOption, setAdjustSelectedOption] = useState(null);
    const [adjustForm, setAdjustForm] = useState({
        warehouse_id: '',
        movement_type_id: '',
        movement_date: localDateISO(),
        document: '',
        quantity: '',
        empenho: '',
        notes: '',
    });

    const stockItemOptions = useMemo(() => {
        const out = [];
        products.forEach((p) => {
            out.push({
                ...p,
                _stockKey: `p:${p.id}`,
                _stockKind: 'product',
            });
        });
        supplies.forEach((s) => {
            out.push({
                ...s,
                _stockKey: `s:${s.id}`,
                _stockKind: 'supply',
            });
        });
        return out;
    }, [products, supplies]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [pr, su, wh, mt] = await Promise.all([
                    productsService.list({ per_page: 1000 }),
                    suppliesService.list({ per_page: 1000 }),
                    warehousesService.list(),
                    movementTypesService.list({ per_page: 500 }),
                ]);
                const prRows = pr.data?.data || pr.data;
                const suRows = su.data?.data || su.data;
                const whRows = wh.data?.data || wh.data;
                const mtRows = mt.data?.data || mt.data;
                if (cancelled) return;
                setProducts(Array.isArray(prRows) ? prRows : []);
                setSupplies(Array.isArray(suRows) ? suRows : []);
                setWarehouses(Array.isArray(whRows) ? whRows : []);
                const arr = Array.isArray(mtRows) ? mtRows : [];
                setMovementTypes(arr);
                const ie = arr.find((t) => String(t.code || '').toUpperCase() === 'IE');
                setDefaultMovementTypeId(ie?.id ?? arr.find((t) => t.direction === 'in')?.id ?? null);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const buildReportParams = useCallback(() => {
        const { period, product, supply, movementTypeId, warehouseId } = filters.applied;
        const params = {
            date_from: period.from,
            date_to: period.to,
            product: product.trim() || undefined,
            supply: supply.trim() || undefined,
            movement_type_id: movementTypeId || undefined,
            warehouse_id: warehouseId || undefined,
        };
        return params;
    }, [filters.applied]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadReportCsv(
                reportsService.stockMovementsCsv(buildReportParams()),
                'movimentacao-estoque.csv',
            );
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao exportar', fallback: 'Não foi possível exportar o relatório' });
        } finally {
            setExporting(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await reportsService.stockMovements(buildReportParams());
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o relatório' });
        } finally {
            setLoading(false);
        }
    }, [buildReportParams, toast]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!extractDrawerOpen || drawerExtractItem == null) {
            return undefined;
        }
        let cancelled = false;
        const extractParams = {
            date_from: filters.applied.period.from,
            date_to: filters.applied.period.to,
        };
        if (drawerExtractItem.kind === 'product') {
            extractParams.product_id = drawerExtractItem.id;
        } else {
            extractParams.supply_id = drawerExtractItem.id;
        }
        setExtractLoading(true);
        setExtractData(null);
        reportsService
            .stockMovementDailyExtract(extractParams)
            .then(({ data }) => {
                if (!cancelled) {
                    setExtractData(data);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    console.error(e);
                    toastApiError(e, { title: 'Erro ao carregar extrato', fallback: 'Não foi possível carregar o extrato diário' });
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setExtractLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [extractDrawerOpen, drawerExtractItem, filters.applied.period.from, filters.applied.period.to, toast]);

    const closeExtractDrawer = useCallback(() => {
        setExtractDrawerOpen(false);
        setDrawerExtractItem(null);
        setDrawerItemLabel('');
        setExtractData(null);
    }, []);

    const openDrawerForRow = useCallback((row) => {
        let extractItem = null;
        if (row.item_kind === 'supply' && row.supply_id != null) {
            extractItem = { kind: 'supply', id: row.supply_id };
        } else if (row.product_id != null) {
            extractItem = { kind: 'product', id: row.product_id };
        }
        if (extractItem == null) {
            return;
        }
        setAdjustmentDrawerOpen(false);
        setDrawerItemLabel(row.item || '-');
        setDrawerExtractItem(extractItem);
        setExtractDrawerOpen(true);
    }, []);

    const rowHasDailyExtract = (row) => row.product_id != null || row.supply_id != null;

    const buildFreshAdjustForm = useCallback(
        () => ({
            warehouse_id: '',
            quantity: '',
            movement_type_id: defaultMovementTypeId != null ? String(defaultMovementTypeId) : '',
            movement_date: localDateISO(),
            document: '',
            empenho: '',
            notes: '',
        }),
        [defaultMovementTypeId],
    );

    const openAdjustmentDrawer = useCallback(() => {
        setExtractDrawerOpen(false);
        setAdjustItemKey(null);
        setAdjustSelectedOption(null);
        setAdjustForm(buildFreshAdjustForm());
        setAdjustmentDrawerOpen(true);
    }, [buildFreshAdjustForm]);

    const handleAdjustFormChange = (e) => {
        setAdjustForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAdjustmentSubmit = async (e) => {
        e.preventDefault();
        if (!adjustSelectedOption) {
            toast({
                title: 'Item obrigatório',
                description: 'Selecione um produto ou insumo.',
                variant: 'error',
            });
            return;
        }
        const qty = parseFloat(String(adjustForm.quantity).replace(',', '.'));
        if (!Number.isFinite(qty) || qty <= 0) {
            toast({ title: 'Quantidade inválida', description: 'Informe um valor maior que zero.', variant: 'error' });
            return;
        }
        const mid = adjustForm.movement_type_id ? parseInt(adjustForm.movement_type_id, 10) : null;
        if (!mid || Number.isNaN(mid)) {
            toast({ title: 'Tipo obrigatório', description: 'Selecione o tipo de movimento.', variant: 'error' });
            return;
        }

        const payload = {
            warehouse_id: parseInt(adjustForm.warehouse_id, 10),
            movement_type_id: mid,
            quantity: qty,
            movement_date: adjustForm.movement_date,
            document: adjustForm.document?.trim() || null,
            empenho: adjustForm.empenho?.trim() || null,
            notes: adjustForm.notes?.trim() || null,
        };

        setAdjustmentLoading(true);
        try {
            if (adjustSelectedOption._stockKind === 'product') {
                await productsService.adjustInventory(adjustSelectedOption.id, payload);
            } else {
                await suppliesService.adjustInventory(adjustSelectedOption.id, payload);
            }
            toast({ title: 'Movimentação registrada', variant: 'success' });
            setAdjustmentDrawerOpen(false);
            setAdjustItemKey(null);
            setAdjustSelectedOption(null);
            setAdjustForm(buildFreshAdjustForm());
            await load();
        } catch (err) {
            console.error(err);
            toastApiError(err, { title: 'Erro ao registrar', fallback: 'Não foi possível registrar a movimentação.' });
        } finally {
            setAdjustmentLoading(false);
        }
    };

    const thBase = 'h-[56px] text-sm font-medium text-gray-500 text-center p-0';
    const tdBase = 'h-[56px] text-sm text-gray-800 p-0';
    const tdCenter = 'tabular-nums';
    const tdEnd = 'justify-end tabular-nums text-right';
    const ROW_HEIGHT = 56;
    const thInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center justify-center';
    const tdInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center';

    const colWidths = [130, 160, 320, 180, 220, 110, 100];
    const colLeft = [0, colWidths[0], colWidths[0] + colWidths[1]];

    const stickyTop = {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: '#f9fafb',
    };
    const stickyTopWithLeft = (left, width) => ({
        ...stickyTop,
        left,
        minWidth: width,
        width,
        zIndex: 11,
    });
    const stickyBody = (left, width) => ({
        position: 'sticky',
        left,
        minWidth: width,
        width,
        background: '#fff',
        zIndex: 5,
    });
    const normalCol = (width) => ({ minWidth: width, width });
    const totalCols = 7;

    const drawerTitle = (
        <div className="space-y-1">
            <div className="text-lg font-semibold leading-snug">{drawerItemLabel || '-'}</div>
            {extractData && (
                <>
                    <div className="text-sm font-normal text-gray-600">{extractData.period_label}</div>
                    <div className="text-sm font-normal text-gray-600">
                        Saldo inicial: {formatQuantity(extractData.opening_balance)}
                    </div>
                </>
            )}
        </div>
    );

    const extractCols = [
        { key: 'date_label', label: 'Data', align: 'left' },
        { key: 'document', label: 'Documento', align: 'left' },
        { key: 'production', label: 'Produção', align: 'right' },
        { key: 'order_out', label: 'Saída de pedido', align: 'right' },
        { key: 'invoice_out', label: 'Saída de NF', align: 'right' },
        { key: 'inventory_in', label: 'Entrada inventário', align: 'right' },
        { key: 'inventory_out', label: 'Saída inventário', align: 'right' },
        { key: 'scrap', label: 'Refugo', align: 'right' },
        { key: 'second_line', label: '2ª linha', align: 'right' },
        { key: 'stock', label: 'Estoque', align: 'right' },
    ];

    return (
        <AppLayout>
            <div className="h-[calc(100vh-8rem)] flex flex-col gap-0">
                <PageHeader
                    title="Movimentação de estoque"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Relatórios' },
                    ]}
                    additionalActions={[
                        toFilterAction({ count: filters.count, onClick: filters.openDrawer }),
                        {
                            label: 'Novo registro',
                            icon: 'new',
                            onClick: openAdjustmentDrawer,
                            disabled: loading,
                        },
                    ]}
                    actions={[
                        toReportExportAction({
                            onExport: handleExport,
                            disabled: loading,
                            exporting,
                        }),
                    ]}
                />
                <div className="overflow-auto flex-1 min-h-0 -mx-4 md:-mx-8 -mb-8">
                    {loading && rows.length === 0 ? null : (
                        <table className="w-full caption-bottom text-sm">
                            <TableHeader className="border-b-0 bg-transparent">
                                <TableRow className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[0], colWidths[0])}>
                                        <div className={cn(thInner, 'report-first-col')}>Data</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[1], colWidths[1])}>
                                        <div className={thInner}>Linha</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[2], colWidths[2])}>
                                        <div className={thInner}>Item</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[3]) }}>
                                        <div className={thInner}>Tipo mov.</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[4]) }}>
                                        <div className={thInner}>Documento</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[5]) }}>
                                        <div className={thInner}>Qtd.</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[6]) }}>
                                        <div className={cn(thInner, 'justify-center border-r-2 border-r-gray-300')}>Ação</div>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow className="border-b-0 hover:bg-transparent">
                                        <TableCell colSpan={totalCols} className="text-center py-8 text-gray-500">
                                            Nenhuma movimentação no período
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row.id} className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[0], colWidths[0])}>
                                                <div className={cn(tdInner, 'report-first-col')}>{formatDateBR(row.date)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[1], colWidths[1])}>
                                                <div className={tdInner}>{row.line}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[2], colWidths[2])}>
                                                <div className={tdInner}>
                                                    <ReportStockItemLink
                                                        productId={row.product_id}
                                                        supplyId={row.supply_id}
                                                        itemKind={row.item_kind}
                                                    >
                                                        {row.item}
                                                    </ReportStockItemLink>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[3])}>
                                                <div className={cn(tdInner, 'whitespace-nowrap')}>{row.type_label}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[4])}>
                                                <div className={cn(tdInner, 'min-w-0')}>
                                                    {row.production_order_id != null ? (
                                                        <ProductionOrderDocumentRef
                                                            productionOrderId={row.production_order_id}
                                                            reportId={row.production_report_id}
                                                        />
                                                    ) : (
                                                        <span className="whitespace-pre-line leading-snug">
                                                            {row.document}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[5])}>
                                                <div className={cn(tdInner, tdCenter, 'justify-center')}>{formatSignedQuantity(row.quantity)}
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[6])}>
                                                <div className={cn(tdInner, 'justify-center')}>
                                                    <button
                                                        type="button"
                                                        onClick={() => openDrawerForRow(row)}
                                                        disabled={!rowHasDailyExtract(row)}
                                                        className={cn(
                                                            'inline-flex items-center justify-center rounded-md border border-gray-300 px-2 py-1 text-gray-700 transition-colors',
                                                            rowHasDailyExtract(row)
                                                                ? 'hover:bg-gray-100'
                                                                : 'opacity-50 cursor-not-allowed',
                                                        )}
                                                        aria-label="Abrir extrato diário"
                                                        title="Abrir extrato diário"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0c0 1.2-4 6-9 6s-9-4.8-9-6 4-6 9-6 9 4.8 9 6z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </table>
                    )}
                </div>
            </div>

            <Drawer open={extractDrawerOpen} onClose={closeExtractDrawer} title={drawerTitle} size="2xl">
                {!extractLoading && extractData?.rows && (
                    <div className="overflow-x-auto -mx-2">
                        <table className="w-full min-w-[920px] text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-600 uppercase tracking-wide">
                                    {extractCols.map((c) => (
                                        <th
                                            key={c.key}
                                            className={cn(
                                                'px-2 py-2 whitespace-nowrap',
                                                c.align === 'right' && 'text-right',
                                            )}
                                        >
                                            {c.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {extractData.rows.map((r) => (
                                    <tr key={r.date} className="border-b border-gray-100 hover:bg-gray-50/50">
                                        {extractCols.map((c) => {
                                            const raw = r[c.key];
                                            const isNum =
                                                c.key !== 'date_label' &&
                                                c.key !== 'document';
                                            return (
                                                <td
                                                    key={c.key}
                                                    className={cn(
                                                        'px-2 py-1.5 text-gray-800',
                                                        c.align === 'right' && 'text-right tabular-nums',
                                                        c.key === 'document' && 'max-w-[220px] break-words whitespace-pre-line',
                                                    )}
                                                    title={c.key === 'document' ? raw : undefined}
                                                >
                                                    {isNum ? formatQuantity(raw) : raw || '-'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!extractLoading && !extractData?.rows && extractDrawerOpen && drawerExtractItem && (
                    <div className="text-center py-10 text-gray-500 text-sm">
                        Nenhum dado de extrato para este período ou falha ao carregar.
                    </div>
                )}
            </Drawer>

            <Drawer
                open={adjustmentDrawerOpen}
                onClose={() => {
                    setAdjustmentDrawerOpen(false);
                    setAdjustItemKey(null);
                    setAdjustSelectedOption(null);
                    setAdjustForm(buildFreshAdjustForm());
                }}
                title="Nova movimentação de estoque"
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setAdjustmentDrawerOpen(false);
                                setAdjustItemKey(null);
                                setAdjustSelectedOption(null);
                                setAdjustForm(buildFreshAdjustForm());
                            }}
                            disabled={adjustmentLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            form="adjustment-form"
                            disabled={adjustmentLoading}
                        >
                            {adjustmentLoading ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <form id="adjustment-form" onSubmit={handleAdjustmentSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Item *</label>
                        <Autocomplete
                            options={stockItemOptions}
                            getOptionLabel={(o) =>
                                o._stockKind === 'product'
                                    ? `Produto · ${o.code} - ${o.description || ''}`
                                    : `Insumo · ${o.code} - ${o.description || ''}`
                            }
                            getOptionValue={(o) => o._stockKey}
                            value={adjustItemKey}
                            onChange={(v, opt) => {
                                setAdjustItemKey(v);
                                setAdjustSelectedOption(opt || null);
                                setAdjustForm((prev) => ({
                                    ...prev,
                                    warehouse_id:
                                        opt?.default_warehouse_id != null
                                            ? String(opt.default_warehouse_id)
                                            : '',
                                }));
                            }}
                            placeholder="Busque produto ou insumo..."
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                        <MaskedInput
                            mask="date"
                            name="movement_date"
                            value={adjustForm.movement_date}
                            onChange={handleAdjustFormChange}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Depósito *</label>
                        <select
                            name="warehouse_id"
                            value={adjustForm.warehouse_id}
                            onChange={handleAdjustFormChange}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Selecione...</option>
                            {warehouses.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {w.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de movimento *</label>
                        <select
                            name="movement_type_id"
                            value={adjustForm.movement_type_id}
                            onChange={handleAdjustFormChange}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Selecione...</option>
                            {movementTypes.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.code} - {t.name} ({t.direction === 'in' ? 'entrada' : 'saída'})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade *</label>
                        <Input
                            type="number"
                            step="0.001"
                            min="0"
                            name="quantity"
                            value={adjustForm.quantity}
                            onChange={handleAdjustFormChange}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                        <Input
                            type="text"
                            name="document"
                            value={adjustForm.document}
                            onChange={handleAdjustFormChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Empenho</label>
                        <Input
                            type="text"
                            name="empenho"
                            value={adjustForm.empenho}
                            onChange={handleAdjustFormChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                        <textarea
                            name="notes"
                            value={adjustForm.notes}
                            onChange={handleAdjustFormChange}
                            rows={3}
                            className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                </form>
            </Drawer>
            <FilterDrawer open={filters.open} onClose={filters.close} onApply={filters.apply} onClear={filters.clear}>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                    <DateRangePicker
                        className="w-full"
                        value={filters.draft.period}
                        onChange={(v) => filters.set('period', v)}
                        aria-label="Período"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Produto</label>
                    <Input value={filters.draft.product} onChange={(e) => filters.set('product', e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Insumo</label>
                    <Input value={filters.draft.supply} onChange={(e) => filters.set('supply', e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de movimento</label>
                    <select
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={filters.draft.movementTypeId}
                        onChange={(e) => filters.set('movementTypeId', e.target.value)}
                    >
                        <option value="">Todos</option>
                        {movementTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Depósito</label>
                    <select
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={filters.draft.warehouseId}
                        onChange={(e) => filters.set('warehouseId', e.target.value)}
                    >
                        <option value="">Todos</option>
                        {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                    </select>
                </div>
            </FilterDrawer>
        </AppLayout>
    );
}

export default StockMovementsReport;
